import type { ReactNode } from 'react';
import { useUiLabels } from '../labels';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Icon } from './icon';

export interface ErrorStateProps {
  /** What failed, e.g. "Konnte nicht geladen werden". */
  title: ReactNode;
  /** What the person can do besides trying again. */
  description?: ReactNode;
  /** Shows the button "Erneut versuchen". */
  onRetry?: () => void;
  /** Less space around it, for tiles and side columns. */
  compact?: boolean;
  className?: string;
}

/**
 * Something could not be loaded. Says so in words with an icon, never only in colour, and offers
 * to try again (docs/ui/design-system.md, "Zustände eingebaut").
 */
export function ErrorState({
  title,
  description,
  onRetry,
  compact = false,
  className,
}: ErrorStateProps) {
  const labels = useUiLabels();
  return (
    <div
      className={cn(
        'flex flex-col items-center px-4 text-center',
        compact ? 'py-6' : 'py-10',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-md border border-danger-line bg-danger-subtle text-danger',
          compact ? 'mb-2 size-8' : 'mb-3 size-10',
        )}
      >
        <Icon name="cloud-off" className={compact ? 'size-4' : 'size-5'} />
      </div>
      <p className={cn('font-medium text-fg', compact ? 'text-sm' : 'text-base')}>{title}</p>
      {description && <p className="mt-1 max-w-sm text-fg-muted">{description}</p>}
      {onRetry && (
        <Button size="sm" icon="refresh-cw" onClick={onRetry} className={compact ? 'mt-3' : 'mt-4'}>
          {labels.retry}
        </Button>
      )}
    </div>
  );
}
